import { useKohde, useKohdeSynkka } from "./Kohdekonteksti";
import { puuteKuvausTeksti } from "./puutekuvausTeksti";
import { useLataus, LatausTila } from "./Lataus";
import { HitsiLinkki, KohdeLinkki, LinjaLinkki } from "./Linkit";
import { naytaPvm } from "./pvm";
import Liitteet from "./Liitteet";
import { Ohje } from "./Ohje";
import { useKirjaustunniste } from "./kirjaustunniste";
import { Hoitaja } from "./Hoitaja";
import { KorvaavaKuittaus, KorvausMerkinta, saaKorvata } from "./KorvaavaKuittaus";
import { useCallback, useEffect, useState } from "react";
import { nayttonimi, nayttonimiPieni, selite, vaihtoehdot } from "./nayttonimi";
import { VASTUU_SUODATTIMET, type VastuuSuodatin,
  api, ApiError, type Kayttaja, type Kohde, type Puute, type HoitajaSuodatin,
  type PuutteenTila, type PuutteenTyyppi, type PuutteenVaikutus,
} from "./api";
import { useOsoiteHaku, lueHaku, haunLuku } from "./osoitehaku";
import { t, virheTeksti, type KaannosAvain } from "./kieli";
import { KAANNOKSET } from "./kaannokset";

/* ═══════════════════════════════════════════════════════════
   Puutteet — mikä on pielessä.

   Oma näkymä, ei hitsauslokin sisällä. "Luovutusvalmius" on eri asia:
   se on lähdeaukot ja dokumentointivelka, jotka ovat putkidomainia
   (isometri, BOM, NDT, painekoe). Tämä on yleinen: havainto, vastuu,
   vaikutus ja ikä — ja se on se osa jonka saha ostaa ilman
   hitsauslokia.

   Ikä on näkyvin sarake tarkoituksella. "On odottanut 47 päivää" on
   se lause joka tekee ulkoisesta aukosta laskutettavan
   (PAATOKSET §havaittu tekee aukosta laskutettavan).
   ═══════════════════════════════════════════════════════════ */

// T439: funktio eikä vakio — nimi ja selite luetaan käyttäjän kielellä piirtohetkellä, ei moduulin latauksessa.
const tyypit = (): { arvo: PuutteenTyyppi; nimi: string; selite?: string }[] => [
  ...(vaihtoehdot("puute_tyyppi").filter((t) => t.arvo !== "punch") as { arvo: PuutteenTyyppi; nimi: string; selite?: string }[]),
  /* ⛔ Punch on POISSA valikosta väliaikaisesti (3.9.2026). Tämä lomake
     kirjoittaa puute-riviä kohde_taulu = 'kohde', eikä yksikään
     punch-näkymä lue sitä — oikeat punchit ovat punch-taulussa linjalla.
     Vaihtoehto joka hukkaa kirjauksen hiljaa on pahempi kuin puuttuva
     vaihtoehto. Palautuu kun tyyppi vaihtaa kohdevalitsimen ja
     kohdetaulun yhdessä: Punch → linjalista → POST /api/linjat/:id/punchit.
     Mitattu: puute.tyyppi = 'punch' on 0 riviä kaikissa instansseissa. */
];

const vaikutukset = () => vaihtoehdot("puute_vaikutus") as { arvo: PuutteenVaikutus; nimi: string; selite?: string }[];

const tyypinNimi = (t: string) => tyypit().find((x) => x.arvo === t)?.nimi ?? t;

type TilaSuodatin = PuutteenTila | "avoimet" | "kaikki";
const TILAT: TilaSuodatin[] = ["avoimet", "avoin", "kaynnissa", "ratkaistu", "hylatty", "korvattu", "kaikki"];

/* Osoitteen arvot API:n omilla nimillä (`kohde_id`, `tila`, `tyyppi`,
   `vaikutus`) — Projekti-Hubin "Puutteet luokittain" osoittaa tänne
   muodossa `?kohde_id=32&tila=avoimet&tyyppi=lahdeaukko&vaikutus=hidastaa`.
   Tuntematon arvo putoaa oletukseen: portti erehtyy kiinni päin. */
function lueSuodatin(p: URLSearchParams) {
  const tila = p.get("tila") as TilaSuodatin | null;
  const tyyppi = p.get("tyyppi") as PuutteenTyyppi | null;
  const vaikutus = p.get("vaikutus") as PuutteenVaikutus | null;
  const vastuu = p.get("vastuu") as VastuuSuodatin | null;
  const hoitaja = p.get("hoitaja");
  const saanto = (p.get("saanto") ?? "").slice(0, 60);
  return {
    // T567: Hubin ryhmä avautuu samalla säännöllä (tunnisteen alkuosa) — vain osoitteesta, ei omaa valitsinta.
    saanto,
    kohde: haunLuku(p, "kohde_id"),
    tila: tila && TILAT.includes(tila) ? tila : "avoimet" as TilaSuodatin,
    tyyppi: tyyppi && tyypit().some((t) => t.arvo === tyyppi) ? tyyppi : "" as PuutteenTyyppi | "",
    vaikutus: vaikutus && vaikutukset().some((v) => v.arvo === vaikutus) ? vaikutus : "" as PuutteenVaikutus | "",
    vastuu: vastuu && VASTUU_SUODATTIMET.includes(vastuu) ? vastuu : "" as VastuuSuodatin | "",
    // 236 (T534): osoitusviestin linkki tuo tänne ?hoitaja=minun.
    hoitaja: (hoitaja === "minun" || hoitaja === "ei" ? hoitaja : "") as HoitajaSuodatin | "",
  };
}

/** Pysäyttävä erottuu värillä; muut eivät kilpaile sen kanssa. */
function Vaikutus({ arvo }: { arvo: PuutteenVaikutus }) {
  const tyyli = arvo === "pysayttaa"
    ? { color: "var(--puna)", fontWeight: 700 }
    : arvo === "hidastaa" ? {} : { opacity: 0.6 };
  return <span style={{ fontSize: 12, ...tyyli }}>
    {vaikutukset().find((v) => v.arvo === arvo)?.nimi ?? arvo}
  </span>;
}

export default function Puutteet({ kayttaja, osoiteHaku, asetaOsoiteHaku }: {
  kayttaja: Kayttaja;
  /** Osoitteen kyselyosa, ks. osoitehaku.ts. */
  osoiteHaku?: string;
  asetaOsoiteHaku?: (haku: string) => void;
}) {
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  // Suodatin alustetaan osoitteesta useState-alustajassa — pakollinen,
  // ei mukavuus (osoitehaku.ts, otsikkokommentti).
  const alku = lueSuodatin(lueHaku(osoiteHaku));
  const [kohde, setKohde] = useState<number | "">(alku.kohde);
  // T426: yläpalkin kohdevalinta ohjaa; osoitteen kohde_id (Hubin linkki) vaihtaa yläpalkin.
  const { ylapalkissa } = useKohdeSynkka(kohde, setKohde,
    { osoitteenKohde: lueSuodatin(lueHaku(osoiteHaku)).kohde, kaikkiSallittu: true });
  /* Testikortti 3221282 kohta 3 (Toni 4.11.2026 klo 11.55): «Kaikki kohteet» -tilassa rivi kertoo, minkä työkohteen puute se on.
     Tunnus luetaan yläpalkin kohdelistasta (sama joukko kuin valitsimessa); kohteeton puute (henkilö, kalusto) jää ilman merkkiä. */
  const { kohteet: valitsimenKohteet } = useKohde();
  const kohteenTunnus = (id: number | null) => id == null ? null : valitsimenKohteet.find((x) => x.id === id)?.tunnus ?? null;
  const [tila, setTila] = useState<TilaSuodatin>(alku.tila);
  const [tyyppi, setTyyppi] = useState<PuutteenTyyppi | "">(alku.tyyppi);
  const [vaikutus, setVaikutus] = useState<PuutteenVaikutus | "">(alku.vaikutus);
  const [vastuu, setVastuu] = useState<VastuuSuodatin | "">(alku.vastuu);
  const [hoitaja, setHoitaja] = useState<HoitajaSuodatin | "">(alku.hoitaja);
  const [saanto, setSaanto] = useState<string>(alku.saanto);
  const [rivit, setRivit] = useState<Puute[] | null>(null);

  useOsoiteHaku(osoiteHaku, asetaOsoiteHaku,
    { kohde_id: kohde, tila, tyyppi, vaikutus, vastuu, hoitaja, saanto },
    (p) => {
      const s = lueSuodatin(p);
      /* 17.10.2026 (Toni klo 12.05): osoite ilman kohdetta ei ylikirjoita yläpalkin kohdetta — valikosta avattuna mountin
       osoitesynkka ajoi yläpalkin koukun jälkeen ja palautti kohteeksi «kaikki», jolloin näkymä ja yläpalkki jäivät eri mieltä. */
      if (p.has("kohde_id")) setKohde(s.kohde);
      setTila(s.tila); setTyyppi(s.tyyppi); setVaikutus(s.vaikutus); setVastuu(s.vastuu); setHoitaja(s.hoitaja); setSaanto(s.saanto);
    });
  const [virhe, setVirhe] = useState<string | null>(null);
  const [lomake, setLomake] = useState(false);

  const [kattavuus, setKattavuus] = useState<{
    tyyppi: string; saanto: string; kohde_taulu: string;
    puutteita: number; kohteita: number;
    populaatio: number | null; kattavuus: number | null;
  }[] | null>(null);

  const klKohteet = useLataus();
  useEffect(() => { klKohteet.seuraa(api.kohteet().then(setKohteet)); }, [klKohteet.kerta]);
  useEffect(() => { api.puutteidenKattavuus().then(setKattavuus).catch(() => setKattavuus([])); }, []);

  const lataa = useCallback(() => {
    api.puutteet({
      ...(kohde !== "" && { kohde_id: kohde }),
      tila,
      ...(tyyppi !== "" && { tyyppi }),
      ...(vaikutus !== "" && { vaikutus }),
      ...(vastuu !== "" && { vastuu }),
      ...(hoitaja !== "" && { hoitaja }),
      ...(saanto !== "" && { saanto }),
    }).then((r) => { setRivit(r); setVirhe(null); })
      .catch((e) => { setRivit([]); setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("pu.haku_virhe")); });
  }, [kohde, tila, tyyppi, vaikutus, vastuu, hoitaja, saanto]);
  useEffect(() => { lataa(); }, [lataa]);

  // T332: nimiehdotuksen vahvistus — samat roolit kuin rekisterin rajapinnassa (REKISTERI_ROOLIT, routes.ts).
  const vainLukija = kayttaja.roolit.every((r) => r === "lukija");
  const saaVahvistaa = kayttaja.roolit.some((r) => ["paakayttaja", "tyonjohtaja", "suunnittelija"].includes(r));
  /* T346: kuvat auki riviltä, yksi kerrallaan — sama kuvio kuin punchilla.
     Kuvan saa lisätä sama joukko joka saa kuitata puutteen: kuittaus on
     tarkoituksella roolittamaton (017 §ratkaisija), eikä kuvan lisääminen
     ole kapeampi teko kuin puutteen sulkeminen. */
  const [kuvatAuki, setKuvatAuki] = useState<number | null>(null);
  async function vahvista(e: NonNullable<Puute["nimiehdotus"]>) {
    try { await api.vahvistaNimiehdotus(e.asu, e.ehdotus_id); lataa(); }
    catch (v) { setVirhe(v instanceof ApiError ? virheTeksti(v.virhe, v.message) : t("pu.vahvistus_virhe")); }
  }

  async function muuta(id: number, muutokset: Parameters<typeof api.paivitaPuute>[1]) {
    try { await api.paivitaPuute(id, muutokset); lataa(); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("pu.muutos_virhe")); }
  }

  /* TUNNUSLUKU ON SUODATUKSEN KOKO, EI SIVUN.
   *
   * Nama laskettiin palautetuista riveista, ja reitilla on `raja`
   * jonka oletus on 200. Ruudulla luki "200 avointa" kun demossa oli
   * 2 260 - kerroin 11. Eika suurinkaan sallittu raja (1000) riita:
   * todellista lukua ei saanut reitista lainkaan.
   *
   * Ja kaksi muuta olivat oikein VAHINGOSSA: pysayttavat lajitellaan
   * ensin, joten ne kaksi mahtuivat sivulle. 250:lla ne olisivat
   * katkenneet hiljaa.
   *
   * Palvelin laskee nyt luvut ikkunafunktiolla ENNEN LIMITia. Rivit
   * ovat yha enintaan `raja` kappaletta - luku ja lista ovat eri
   * asia, ja vain edellinen valehteli. */
  const ensimmainen = (rivit ?? [])[0];
  const avoimia     = ensimmainen?.yhteensa ?? 0;
  const pysayttavia = ensimmainen?.yhteensa_pysayttaa ?? 0;
  const ulkoisia    = ensimmainen?.yhteensa_ulkoisia ?? 0;
  /* Naytetaanko lista katkaistuna: rivit < luku tarkoittaa etta sivu
     ei kata kaikkea, ja se sanotaan aareen eika jateta paateltavaksi. */
  const katkaistu   = (rivit?.length ?? 0) < avoimia;

  return (
    <>
      <header className="head">
        <div className="head__rivi"><h1>{t("valikko.puutteet")}</h1></div>
      </header>
      <LatausTila l={klKohteet} nimi="lataus.kohteet" />

      {/* Ohje ylös (Toni 24.9.2026; menee asiakkaalle). «Kuka» = /api/puutteet (ei roolirajaa, saaNahdaKohteen,
          puutteenRajaus), /api/liitteet (lisäys ja poisto roolein), /api/henkilorekisteri/vahvista. Alalaidan
          selite siirtyi tähän. */}
      <Ohje
        kuka={<>{t("pu.ohje.kuka")}</>}
        luvut={<>{t("pu.ohje.luvut")}</>} />

      {/* Kattavuusrivi.
        *
        * Sääntö joka osuu 100 %:iin kohdejoukostaan on kattavuusluku
        * eikä havainto, ja se jätetään jonosta pois. Mutta piilotus
        * ilman tätä riviä olisi näkymätön tyhjä -- lukija ei tietäisi
        * että jotain jätettiin pois eikä miksi.
        *
        * Kattavuus näytetään JOKAISELLE säännölle, ei vain
        * poikkeaville: silloin seuraava 100 %:n sääntö näkyy
        * itsestään eikä sitä tarvitse löytää. */}
      {/* T537 jatko (Toni 13.10.2026 b: «Puutteet-sisältö taitteen yläpuolelle 375 px:ssä»): kattavuusrivi suljetun avauksen
          taakse — tieto säilyy sivulla, mutta yksi rivi per sääntö ei enää työnnä listaa taitteen alle (demo 989 px, pekilo 1 644 px). */}
      {kattavuus && kattavuus.length > 0 && (
        <details className="pu-kattavuus" data-puute-kattavuus="">
        <summary>{t("pu.kattavuus_otsikko", { n: kattavuus.length })}</summary>
        <p className="rekisteririvi">
          {kattavuus.map((k, i) => (
            <span key={k.tyyppi + k.saanto + k.kohde_taulu}>
              {i > 0 && " · "}
              <span title={selite("havainnointisaanto", k.saanto)}>{nayttonimi("havainnointisaanto_lyhyt", k.saanto.split(":")[0])}</span>{" "}
              <strong>
                {k.kattavuus === null ? "?" : k.kattavuus + " %"}
              </strong>
              {" "}({k.kohteita}/{k.populaatio ?? "?"} {nayttonimiPieni("kohde_taulu", k.kohde_taulu)})
              {k.kattavuus !== null && k.kattavuus >= 100
                && (k.populaatio ?? 0) >= 10 && ` — ${t("pu.jonosta_pois")}`}
            </span>
          ))}
          <br />
          {t("pu.kattavuus_selite")}
        </p>
        </details>
      )}

      {/* Toni 17.10.2026 klo 12.35: suodattimet ja «Kirjaa havainto» yhdelle riville (≥ 1280 px). Valikko mitoitetaan sisällön mukaan
          (min 6–8 rem, max 16 rem), pitkä valinta katkeaa ellipsillä ja koko teksti on title-attribuutissa. */}
      <div className="suodatinrivi suodatinrivi--tiivis" data-suodatinrivi="puutteet">
        {!ylapalkissa && <label className="dim" style={{ fontSize: 13 }}>{t("pu.kohde")}{" "}
          <select value={kohde} onChange={(e) => setKohde(e.target.value ? Number(e.target.value) : "")}>
            <option value="">{t("pu.kaikki_kohteet")}</option>
            {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
          </select>
        </label>}
        <label className="dim" style={{ fontSize: 13 }}>{t("pu.tila")}{" "}
          <select value={tila} onChange={(e) => setTila(e.target.value as typeof tila)}>
            <option value="avoimet">{t("pu.tila.avoimet")}</option>
            <option value="avoin">{t("pu.tila.vain_avoin")}</option>
            <option value="kaynnissa">{t("pu.tila.kaynnissa")}</option>
            <option value="ratkaistu">{t("pu.tila.ratkaistut")}</option>
            <option value="hylatty">{t("pu.tila.hylatyt")}</option>
            <option value="korvattu">{t("pu.tila.korvatut")}</option>
            <option value="kaikki">{t("pu.kaikki")}</option>
          </select>
        </label>
        <label className="dim" style={{ fontSize: 13 }}>{t("pu.tyyppi")}{" "}
          <select value={tyyppi} title={tyypit().find((x) => x.arvo === tyyppi)?.nimi ?? t("pu.kaikki")} onChange={(e) => setTyyppi(e.target.value as PuutteenTyyppi | "")}>
            <option value="">{t("pu.kaikki")}</option>
            {tyypit().map((t) => <option key={t.arvo} value={t.arvo} title={t.selite}>{t.nimi}{t.selite ? ` — ${t.selite}` : ""}</option>)}
          </select>
        </label>
        {/* Vaikutus on Hubin luokittelun toinen akseli: ilman sitä
            Hubin rivi "HIDASTAA · lahdeaukko 1709" ei avaudu samaksi
            joukoksi. */}
        <label className="dim" style={{ fontSize: 13 }}>{t("pu.vaikutus")}{" "}
          <select value={vaikutus} title={vaikutukset().find((x) => x.arvo === vaikutus)?.nimi ?? t("pu.kaikki")} onChange={(e) => setVaikutus(e.target.value as PuutteenVaikutus | "")}>
            <option value="">{t("pu.kaikki")}</option>
            {vaikutukset().map((v) => <option key={v.arvo} value={v.arvo} title={v.selite}>{v.nimi}{v.selite ? ` — ${v.selite}` : ""}</option>)}
          </select>
        </label>
        {/* 10.10.2026 Toni (C): «mikä vaatii toimia» — kenen pöydällä puute on. */}
        <label className="dim" style={{ fontSize: 13 }}>{t("pu.vastuu")}{" "}
          <select value={vastuu} data-suodatin="vastuu" onChange={(e) => setVastuu(e.target.value as VastuuSuodatin | "")}>
            <option value="">{t("pu.kaikki")}</option>
            {VASTUU_SUODATTIMET.map((v) => <option key={v} value={v}>{t(`pu.vastuu_suodatin.${v}`)}</option>)}
          </select>
        </label>
        {saanto && (
          <span className="merkki" data-suodatin-saanto={saanto}>{t("pu.saanto")}: {
            // 18.10.2026 (vikakoe 4: «sulatus puuttuu» suomeksi viron ja liettuan sirussa): säännön nimi samasta avaimesta kuin Hubin jonossa;
            // tuntematon sääntö näkyy tunnuksena.
            `hub.saanto.${saanto}` in KAANNOKSET ? t(`hub.saanto.${saanto}` as KaannosAvain) : saanto.replace(/_/g, " ")}{" "}
            <button type="button" className="hoitaja__linkki" onClick={() => setSaanto("")} aria-label={t("pu.saanto_pois")}>✕</button>
          </span>
        )}
        {/* 236 (T534): kuka hoitaa — «minä» työnjohtajan omaksi listaksi, «ei kukaan» yhteiseksi jonoksi. */}
        <label className="dim" style={{ fontSize: 13 }}>{t("hoito.suodatin")}{" "}
          <select value={hoitaja} data-suodatin="hoitaja" onChange={(e) => setHoitaja(e.target.value as HoitajaSuodatin | "")}>
            <option value="">{t("pu.kaikki")}</option>
            <option value="minun">{t("hoito.suodatin.minun")}</option>
            <option value="ei">{t("hoito.suodatin.ei")}</option>
          </select>
        </label>
        <button className="nappi nappi--paa" onClick={() => setLomake((v) => !v)}>
          {lomake ? t("hl.peruuta") : t("pu.kirjaa_havainto")}
        </button>
      </div>

      {lomake && (
        <KirjaaPuute
          kohteet={kohteet}
          oletusKohde={kohde}
          valmis={() => { setLomake(false); lataa(); }}
          virheeksi={setVirhe}
        />
      )}

      {virhe && <p className="huom" role="status">{virhe}</p>}

      {/* Tunnuslukurivi piirretään myös nollalla: nolla riviä on
          mittaustulos ("suodatus ei osunut mihinkään"), ja Hubin
          luku on voitava verrata siihen (`data-rivit`, kavely.mjs). */}
      {rivit && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "0 0 12px" }}>
          <Luku arvo={avoimia} teksti={tila === "avoimet" ? t("pu.luku.avointa") : t("pu.luku.rivia")} joukko="puutteet" />
          <Luku arvo={pysayttavia} teksti={t("pu.luku.pysayttaa")} korosta={pysayttavia > 0} />
          <Luku arvo={ulkoisia} teksti={t("pu.luku.ulkoisia")} korosta={ulkoisia > 0} />
          {/* Katkaisu sanotaan aareen: luku on suodatuksen koko, lista
              on sivu. Ilman tata lukija olettaa etta lista on luku. */}
          {katkaistu && (
            <span className="dim" style={{ alignSelf: "center", marginLeft: 4 }}>
              {t("pu.katkaistu", { n: rivit?.length ?? 0 })}
            </span>
          )}
        </div>
      )}

      {rivit === null && <p className="dim">{t("yleinen.ladataan")}</p>}
      {/* Toni 18.10.2026 klo 18.35: kenttätili näkee vain omat puutteensa (palvelimen rajaus, 1dda63a), ja pekilossa lista on silloin
          usein tyhjä — tyhjä lista näyttää kentällä viialta, joten se sanoo miksi ja minne punchit kuuluvat. */}
      {rivit && rivit.length === 0 && !virhe && kayttaja.kenttatili
        ? <p className="dim" data-kenttatili-tyhja>{t("pu.kentta_tyhja")} <a href="#/selailu">{t("valikko.selailu")} ›</a></p>
        : rivit && rivit.length === 0 && !virhe && <p className="dim">{t("pu.ei_puutteita")}</p>}

      {rivit && rivit.length > 0 && (
        /* T677 A (Toni 27.10.2026 klo 09.08): taulukko vierii omassa ikkunassaan (vaakapalkki aina näkyvissä), toimintosarake kiinni
           oikeassa reunassa ja linjatunnus yhdellä rivillä. */
        <div className="taulu-ikkuna" data-taulu-ikkuna="puutteet">
          <table className="tbl" style={{ fontSize: 13 }}>
            <thead><tr>
              <th>{t("pu.vaikutus")}</th><th>{t("pu.tyyppi")}</th><th>{t("pu.mita")}</th><th>{t("pu.kohde")}</th>
              <th>{t("pu.vastuu")}</th><th style={{ textAlign: "right" }}>{t("pu.havaittu")}</th><th>{t("pu.tila")}</th><th>{t("pu.kuvat")}</th><th className="sarake-toiminnot"></th>
            </tr></thead>
            <tbody>
              {rivit.map((p) => (
                <tr key={p.id}>
                  <td><Vaikutus arvo={p.vaikutus} /></td>
                  <td className="dim" >{tyypinNimi(p.tyyppi)}</td>
                  {/* 144: erääntyminen on oma arvonsa, ja se muotoillaan tässä.
                      Vanhoilla riveillä (ennen 144:ää) päiväys on yhä kuvauksen
                      sisällä — sitä ei kaiveta sieltä ulos arvaamalla, vaan rivi
                      näyttää sen sellaisena kuin se kirjattiin. */}
                  <td>
                    {puuteKuvausTeksti(p.kuvaus)}
                    {p.eraantyy && (
                      <span className="mono dim"> {naytaPvm(p.eraantyy)}</span>
                    )}
                  </td>
                  <td className="mono sarake-tunniste" style={{ fontSize: 12 }} data-puute-kohde={p.id} data-puute-taulu={p.kohde_taulu}>
                    {kohde === "" && kohteenTunnus(p.liittyva_kohde_id) && (
                      <span data-puute-tyokohde={p.liittyva_kohde_id ?? ""}><KohdeLinkki tunnus={kohteenTunnus(p.liittyva_kohde_id)} /> · </span>
                    )}
                    {/* T332: linja on linkki — rivin ainoa käyttö on mennä sille linjalle ja korjata. */}
                    {p.linja_tunnus && p.kohde_taulu === "hitsi"
                      ? <HitsiLinkki linja={p.linja_tunnus} hitsiId={p.kohde_id} teksti={p.kohteen_nimi ?? p.linja_tunnus} />
                      : p.linja_tunnus
                      ? <><LinjaLinkki tunnus={p.linja_tunnus} />{(p.kohteen_nimi ?? "").startsWith(p.linja_tunnus)
                          ? (p.kohteen_nimi ?? "").slice(p.linja_tunnus.length) : ""}</>
                      : p.kohteen_nimi ?? <span className="dim">{nayttonimi("kohde_taulu", p.kohde_taulu)} {p.kohde_id}</span>}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {p.vastuu ? nayttonimi("puute_vastuu", p.vastuu) : (p.vastuu_ulkoinen ? <span className="dim">{t("hl.ulkoinen")}</span> : "")}
                    {p.vastuu_ulkoinen && p.vastuu && <span className="dim"> · {t("hl.ulkoinen")}</span>}
                  </td>
                  {/* Ikä on se luku joka tekee aukosta laskutettavan.

                      ⚠ Mutta sarake on "Havaittu", ei "Ikä": arvo on
                      `current_date - havaittu`, eli aika siitä kun
                      havainnointi näki asian. Havainto ei voi edeltää
                      ilmiötä, joten luku on ALARAJA — heinäkuinen aukko
                      lukee "7 vrk" jos cron ajoi ensimmäisen kerran
                      viikko sitten.

                      Kello käynnistyy havainnosta TARKOITUKSELLA
                      (PAATOKSET §havaittu tekee aukosta laskutettavan):
                      dokumentoitu aukko on laskutettava. Otsikko sanoo
                      nyt sen mitä luku on, jottei lukija lue siitä
                      ilmiön ikää. */}
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums",
                               fontWeight: p.ika_vrk >= 30 && (p.tila === "avoin" || p.tila === "kaynnissa") ? 700 : 400 }}>
                    {t("pu.vrk", { n: p.ika_vrk })}
                  </td>
                    <td style={{ fontSize: 12 }}>
                      <span title={selite("puute_tila", p.tila)}>{nayttonimi("puute_tila", p.tila)}</span>
                      <KorvausMerkinta puute={p} />

                      {/* C3 (18.9.2026): suljetulla rivillä sanotaan KUKA sulki.
                          Tyhjä kuittaaja ei ole unohdus vaan sääntö: 089 sulkee
                          puutteen kun kohde lakkaa varaamasta, eikä kuittausta ole
                          kellään. Demossa 2 349 hylätystä jokainen on tätä lajia, ja
                          ruudulla se on näyttänyt puuttuvalta merkinnältä. */}
                      {(p.tila === "ratkaistu" || p.tila === "hylatty") && (
                        <div className="dim" style={{ marginTop: 2 }}
                             title={p.ratkaisija
                               ? t("pu.kuittasi_title")
                               : t("pu.saanto_sulki_title")}>
                          {p.ratkaisija ? t("pu.kuittasi", { nimi: p.ratkaisija }) : t("pu.saanto_sulki")}
                        </div>
                      )}
                      {/* Kertyma signaalina. Sama asia hylattyna kolmesti ei
                          kerro sitkeasta puutteesta vaan vaarasta saannosta:
                          ihminen on arvioinut sen kolmesti turhaksi ja
                          automaatti nostaa sen yha. Mahdollinen vasta 091:n
                          jalkeen, koska suljettu ei enaa esta uutta nostoa. */}
                      {p.hylatty_kertaa > 0 && (
                        <div className={p.hylatty_kertaa >= 2 ? "merkki merkki--vika" : "dim"}
                             style={{ fontSize: 11, marginTop: 2 }}
                             title={p.hylatty_kertaa >= 2
                               ? t("pu.hylatty_monta_title")
                               : t("pu.hylatty_kerran_title")}>
                          {t("pu.hylatty_n", { n: p.hylatty_kertaa })}
                          {p.hylatty_kertaa >= 2 && ` — ${t("pu.tarkista_saanto")}`}
                        </div>
                      )}
                    </td>
                  <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                    <button className="nappi nappi--kevyt" data-kuvat={p.id}
                            title={t("pu.kuvat_title")}
                            onClick={() => setKuvatAuki(kuvatAuki === p.id ? null : p.id)}>
                      {kuvatAuki === p.id ? t("hl.sulje") : p.liitteita ? t("hl.kuvat_n", { n: p.liitteita }) : t("hl.lisaa_kuva")}
                    </button>
                  </td>
                  <td className="sarake-toiminnot" style={{ whiteSpace: "nowrap" }} data-puute-toiminnot={p.id}>
                    {/* 236 (T534): kuka hoitaa — toimintosarakkeessa nappien yläpuolella yhtenä tekstirivinä, jottei kapea
                        Tila-sarake rivity (Toni 13.10.2026: rivin korkeus ei saa kasvaa T534:n takia; mitattu 73 → 89 px Tila-sarakkeessa). Omalla rivillään nappien yläpuolella. */}
                    {(p.tila === "avoin" || p.tila === "kaynnissa" || p.hoitaja) && (<div>
                      <Hoitaja taulu="puute" id={p.id} hoitaja={p.hoitaja} kayttaja={kayttaja} muuttui={lataa}
                               saaKirjoittaa={(p.tila === "avoin" || p.tila === "kaynnissa") && !vainLukija} />
                    </div>)}
                    {(p.tila === "avoin" || p.tila === "kaynnissa") && (
                      <>
                        {p.nimiehdotus && saaVahvistaa && (
                          <><button className="nappi nappi--paa" data-nimiehdotus={p.id}
                                    title={t("pu.ehdotus_title", { asu: p.nimiehdotus.asu, nimi: p.nimiehdotus.ehdotus })}
                                    onClick={() => void vahvista(p.nimiehdotus!)}>{t("pu.hyvaksy_ehdotus", { nimi: p.nimiehdotus.ehdotus })}</button>{" "}</>
                        )}
                        {p.tila === "avoin" && (
                          <button className="nappi nappi--kevyt"
                                  onClick={() => void muuta(p.id, { tila: "kaynnissa" })}>{t("pu.aloitettu")}</button>
                        )}{" "}
                        <button className="nappi nappi--kevyt"
                                onClick={() => void muuta(p.id, { tila: "ratkaistu" })}>{t("pu.ratkaistu")}</button>{" "}
                        <button className="nappi nappi--kevyt"
                                onClick={() => void muuta(p.id, { tila: "hylatty" })}>{t("pu.hylkaa")}</button>{" "}
                        {saaKorvata(kayttaja) && <KorvaavaKuittaus puute={p} muuttui={lataa} />}
                      </>
                    )}
                    {(p.tila === "ratkaistu" || p.tila === "hylatty") && (
                      <button className="nappi nappi--kevyt"
                              onClick={() => void muuta(p.id, { tila: "avoin" })}>{t("pu.avaa_uudelleen")}</button>
                    )}
                  </td>
                </tr>
              )).flatMap((rivi, i) => {
                const p = rivit[i]!;
                return kuvatAuki === p.id
                  ? [rivi,
                     <tr key={`kuvat-${p.id}`} data-kuvarivi={p.id}>
                       <td colSpan={9}>
                         <Liitteet kohdeTaulu="puute" kohdeId={p.id} saaMuokata
                                   otsikko={t("pu.kuvat")} muuttui={lataa} />
                       </td>
                     </tr>]
                  : [rivi];
              })}
            </tbody>
          </table>
        </div>
      )}

    </>
  );
}

function Luku({ arvo, teksti, korosta = false, joukko }: {
  arvo: number; teksti: string; korosta?: boolean;
  /** Kävelytarkistuksen sopimus: `data-rivit` + `data-joukko` on se
   *  luku johon Projekti-Hubin `data-luku` verrataan. */
  joukko?: string;
}) {
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 5, padding: "6px 12px",
      border: "1px solid var(--viiva)",
      borderLeft: `3px solid ${korosta ? "var(--puna)" : "var(--viiva)"}`, fontSize: 13 }}>
      <b style={{ fontSize: 18, fontVariantNumeric: "tabular-nums" }}
         {...(joukko ? { "data-rivit": arvo, "data-joukko": joukko } : {})}>{arvo}</b>
      <span className="dim">{teksti}</span>
    </span>
  );
}

/**
 * Havainnon kirjaus.
 *
 * Kohde on kohde-taulu eikä linja: linja on hitsauslokin käsite, eikä
 * sitä ole siinä instanssissa jolle tämä moduuli myydään yksin.
 * Linjaan ja hitsiin kiinnittyvät puutteet syntyvät hitsauslokista ja
 * automaattihavainnoinnista.
 */
function KirjaaPuute({ kohteet, oletusKohde, valmis, virheeksi }: {
  kohteet: Kohde[];
  oletusKohde: number | "";
  valmis: () => void;
  virheeksi: (v: string | null) => void;
}) {
  const [kohde, setKohde] = useState<number | "">(oletusKohde);
  // 7.10.2026 (Norjan erä): valinnainen linja. Ilman sitä puute kohdistuu kohteeseen kuten ennen; linjaa ei tarvinnut enää
  // kirjoittaa kuvaukseen. Linjat haetaan valitusta kohteesta (vain jos hitsausloki on käytössä — muuten lista jää tyhjäksi).
  const [linjaTeksti, setLinjaTeksti] = useState("");
  const [linjat, setLinjat] = useState<{ id: number; tunnus: string }[]>([]);
  useEffect(() => {
    setLinjaTeksti(""); setLinjat([]);
    if (kohde === "") return;
    api.linjat(undefined, kohde, 2000).then((l) => setLinjat(l.map((x) => ({ id: x.id, tunnus: x.tunnus })))).catch(() => setLinjat([]));
  }, [kohde]);
  const [tyyppi, setTyyppi] = useState<PuutteenTyyppi>("lahdeaukko");
  const [kuvaus, setKuvaus] = useState("");
  const [vaikutus, setVaikutus] = useState<PuutteenVaikutus>("hidastaa");
  const [vastuu, setVastuu] = useState("");
  const [ulkoinen, setUlkoinen] = useState(false);
  const [havaittu, setHavaittu] = useState("");
  const [tallentaa, setTallentaa] = useState(false);
  const [tunniste, vaihdaTunniste] = useKirjaustunniste();   // T323

  const tallenna = async () => {
    if (kohde === "") { virheeksi(t("pu.valitse_kohde")); return; }
    if (!kuvaus.trim()) { virheeksi(t("pu.kerro_mika")); return; }
    const linja = linjaTeksti.trim() ? linjat.find((l) => l.tunnus === linjaTeksti.trim()) : null;
    if (linjaTeksti.trim() && !linja) { virheeksi(t("pu.linjaa_ei_loydy", { linja: linjaTeksti.trim() })); return; }
    setTallentaa(true);
    try {
      await api.luoPuute({
        kirjaustunniste: tunniste,
        tyyppi, ...(linja ? { kohde_taulu: "linja", kohde_id: linja.id } : { kohde_taulu: "kohde", kohde_id: kohde }), kuvaus: kuvaus.trim(),
        vaikutus, vastuu: vastuu.trim() || null, vastuu_ulkoinen: ulkoinen,
        ...(havaittu && { havaittu }),
      });
      vaihdaTunniste();
      virheeksi(null);
      valmis();
    } catch (e) {
      virheeksi(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("hl.kirjaus_ei_onnistunut"));
    } finally { setTallentaa(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 16 }}>
      <h3>{t("pu.kirjaa_havainto")}</h3>
      <div className="asiakas__lomake">
        <label>{t("pu.kohde")}
          <select value={kohde} onChange={(e) => setKohde(e.target.value ? Number(e.target.value) : "")}>
            <option value="">{t("hl.valitse_viivat")}</option>
            {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
          </select>
        </label>
        {linjat.length > 0 && (
          <label>{t("pu.linja_valinnainen")}
            <input value={linjaTeksti} list="puute-linjat" data-kentta="puute-linja" placeholder={t("pu.tyhja_koko_kohde")}
                   onChange={(e) => setLinjaTeksti(e.target.value)} />
            <datalist id="puute-linjat">{linjat.map((l) => <option key={l.id} value={l.tunnus} />)}</datalist>
          </label>
        )}
        <label>{t("pu.tyyppi")}
          <select value={tyyppi} onChange={(e) => setTyyppi(e.target.value as PuutteenTyyppi)}>
            {tyypit().map((t) => <option key={t.arvo} value={t.arvo} title={t.selite}>{t.nimi}{t.selite ? ` — ${t.selite}` : ""}</option>)}
          </select>
        </label>
        <label>{t("pu.mika_pielessa")}
          <input value={kuvaus} onChange={(e) => setKuvaus(e.target.value)}
                 placeholder={t("pu.mika_pielessa_esim")} />
          {/* Toni 13.10.2026: vastuu kielestä on kirjoittajalla — ohje kentän alle käyttäjän kielellä. */}
          <span className="huom" data-kirjoitusohje="" style={{ display: "block", margin: "4px 0 0" }}>{t("kirjoitusohje.korjaava")}</span>
        </label>
        <label>{t("pu.vaikutus")}
          <select value={vaikutus} onChange={(e) => setVaikutus(e.target.value as PuutteenVaikutus)}>
            {vaikutukset().map((v) => <option key={v.arvo} value={v.arvo} title={v.selite}>{v.nimi}{v.selite ? ` — ${v.selite}` : ""}</option>)}
          </select>
        </label>
        <label>{t("pu.vastuu")}
          <input value={vastuu} onChange={(e) => setVastuu(e.target.value)} placeholder={t("pu.vastuu_esim")} />
        </label>
        {/* Havaintohetki, ei kirjaushetki: vaateessa merkitsee havainto. */}
        <label>{t("pu.havaittu")}
          <input type="date" value={havaittu} onChange={(e) => setHavaittu(e.target.value)} />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={ulkoinen} onChange={(e) => setUlkoinen(e.target.checked)} />
          {t("pu.ulkoinen_vastuu")}
        </label>
        <button className="nappi nappi--paa" disabled={tallentaa} onClick={() => void tallenna()}>
          {tallentaa ? t("pu.tallennetaan") : t("pu.kirjaa")}
        </button>
      </div>
    </section>
  );
}
